// src/pages/Appli/AppliAdmin/VisitorCenter/components/VisitorAnalyticsKpis.jsx

import React from "react";

import {
  FiArrowDownRight,
  FiArrowUpRight,
  FiClock,
  FiEye,
  FiMessageCircle,
  FiMinus,
  FiPhoneCall,
  FiRepeat,
  FiUsers,
  FiZap,
} from "react-icons/fi";

import {
  formatCompactNumber,
  formatDuration,
  formatPercentage,
  safeNumber,
} from "../utils/visitorFormatters";

const TONE_CLASSES = {
  blue: {
    icon:
      "bg-blue-50 text-blue-600",

    border:
      "border-blue-100",
  },

  cyan: {
    icon:
      "bg-cyan-50 text-cyan-600",

    border:
      "border-cyan-100",
  },

  violet: {
    icon:
      "bg-violet-50 text-violet-600",

    border:
      "border-violet-100",
  },

  emerald: {
    icon:
      "bg-emerald-50 text-emerald-600",

    border:
      "border-emerald-100",
  },

  amber: {
    icon:
      "bg-amber-50 text-amber-600",

    border:
      "border-amber-100",
  },

  rose: {
    icon:
      "bg-rose-50 text-rose-600",

    border:
      "border-rose-100",
  },
};

function ComparisonBadge({
  value,
  inverse = false,
}) {
  const variation =
    safeNumber(value);

  const stable =
    variation === 0;

  const rising =
    variation > 0;

  const positive = inverse
    ? variation < 0
    : variation > 0;

  const Icon = stable
    ? FiMinus
    : rising
      ? FiArrowUpRight
      : FiArrowDownRight;

  const className = stable
    ? "bg-slate-100 text-slate-500"
    : positive
      ? "bg-emerald-50 text-emerald-700"
      : "bg-red-50 text-red-700";

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-extrabold ${className}`}
      title="Variation par rapport à la période précédente"
    >
      <Icon />

      {variation > 0
        ? "+"
        : ""}

      {variation.toLocaleString(
        "fr-FR",
        {
          maximumFractionDigits: 1,
        },
      )}
      %
    </span>
  );
}

function AnalyticsKpiCard({
  icon: Icon,
  label,
  value,
  description,
  comparison,
  tone = "blue",
  comparisonInverse = false,
}) {
  const toneClasses =
    TONE_CLASSES[tone] ||
    TONE_CLASSES.blue;

  return (
    <article
      className={`rounded-[24px] border bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${toneClasses.border}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-bold text-slate-500">
            {label}
          </p>

          <p className="mt-2 text-3xl font-extrabold tracking-tight text-[#123B57]">
            {value}
          </p>
        </div>

        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-xl ${toneClasses.icon}`}
        >
          <Icon />
        </div>
      </div>

      <div className="mt-4 flex items-end justify-between gap-3">
        <p className="text-xs leading-5 text-slate-400">
          {description}
        </p>

        <ComparisonBadge
          value={comparison}
          inverse={
            comparisonInverse
          }
        />
      </div>
    </article>
  );
}

export default function VisitorAnalyticsKpis({
  summary = {},
  comparison = {},
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <AnalyticsKpiCard
        icon={FiUsers}
        label="Sessions"
        value={formatCompactNumber(
          summary.sessions,
        )}
        description="Nombre de visites démarrées sur la période."
        comparison={
          comparison.sessions
        }
        tone="blue"
      />

      <AnalyticsKpiCard
        icon={FiEye}
        label="Pages vues"
        value={formatCompactNumber(
          summary.page_views,
        )}
        description={`${safeNumber(
          summary.average_pages_per_session,
        ).toLocaleString(
          "fr-FR",
          {
            maximumFractionDigits: 2,
          },
        )} page(s) par session.`}
        comparison={
          comparison.page_views
        }
        tone="cyan"
      />

      <AnalyticsKpiCard
        icon={FiRepeat}
        label="Visiteurs"
        value={formatCompactNumber(
          summary.unique_visitors,
        )}
        description={`${formatCompactNumber(
          summary.returning_visitors,
        )} visiteur(s) déjà venu(s).`}
        comparison={
          comparison.unique_visitors
        }
        tone="violet"
      />

      <AnalyticsKpiCard
        icon={FiClock}
        label="Durée moyenne"
        value={formatDuration(
          summary.average_duration_seconds,
        )}
        description="Temps moyen entre le début et la dernière activité."
        comparison={
          comparison.average_duration_seconds
        }
        tone="amber"
      />

      <AnalyticsKpiCard
        icon={FiZap}
        label="Sessions engagées"
        value={formatCompactNumber(
          summary.engaged_sessions,
        )}
        description={`${formatPercentage(
          summary.engagement_rate,
        )} des sessions ont interagi.`}
        comparison={
          comparison.engaged_sessions
        }
        tone="emerald"
      />

      <AnalyticsKpiCard
        icon={FiEye}
        label="Taux de rebond"
        value={formatPercentage(
          summary.bounce_rate,
        )}
        description="Sessions ayant consulté une seule page."
        comparison={
          comparison.bounce_rate
        }
        comparisonInverse
        tone="rose"
      />

      <AnalyticsKpiCard
        icon={FiMessageCircle}
        label="Conversations"
        value={formatCompactNumber(
          summary.conversations,
        )}
        description={`${formatPercentage(
          summary.conversation_rate,
        )} des sessions ont écrit.`}
        comparison={
          comparison.conversations
        }
        tone="blue"
      />

      <AnalyticsKpiCard
        icon={FiPhoneCall}
        label="Demandes de rappel"
        value={formatCompactNumber(
          summary.callback_requests,
        )}
        description={`${formatCompactNumber(
          summary.converted_callbacks,
        )} demande(s) convertie(s).`}
        comparison={
          comparison.callback_requests
        }
        tone="emerald"
      />
    </div>
  );
}